import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import DOMPurify from 'dompurify';
import { Application, getApplication, validateApplicationCallbackUri } from '../../api/application';
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import { t } from 'i18next';

const getRedirectParams = () => {
  const urlParams = queryString.parse(globalThis.location.search);
  const redirectUri = DOMPurify.sanitize(urlParams.app_redirect_uri as string);
  const code = DOMPurify.sanitize(urlParams.code as string);
  const state = DOMPurify.sanitize(urlParams.state as string);
  const clientId = DOMPurify.sanitize(urlParams.client_id as string);

  return { redirectUri, code, state, clientId };
}

export const useRedirectData = () => {
  const navigate = useNavigate();
  const currentCulture = useSelector((state: AppState) => state.cultures.currentCulture);

  const [isLoading, setIsLoading] = useState(true);
  const [finalRedirectUri, setFinalRedirectUri] = useState('');
  const [application, setApplication] = useState<Application | null>(null);

  useEffect(() => {
    const getDataForRedirect = async () => {
      try {
        const { redirectUri, code, state, clientId } = getRedirectParams();
        if (!redirectUri || !code || !state || !clientId) {
          navigate('/error?error_description=Invalid query params', { replace: true });
          return;
        }

        const [baseRedirectUri, existingParams] = redirectUri.split('?');

        const isValid = await validateApplicationCallbackUri(clientId, baseRedirectUri);
        if (!isValid) {
          navigate('/error?error_description=Callback URL mismatch', { replace: true });
          return;
        }

        const params = new URLSearchParams(existingParams || '');
        params.set('code', code);
        params.set('state', state);
        const fullRedirect = `${baseRedirectUri}?${params.toString()}`;
        setFinalRedirectUri(fullRedirect);

        const app = await getApplication(clientId, currentCulture);
        if (app) {
          setApplication(app);
        }

        setIsLoading(false);
      }
      catch (error) {
        navigate('/error?error_description=Error fetching application data', { replace: true });
      }
    }

    getDataForRedirect();
  }, []);

  return { isLoading, application, finalRedirectUri };
}
